import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowRight,
  Target,
  Users,
  Heart,
  TrendingUp,
  CheckCircle2,
  Lightbulb,
  Search,
  Mail,
  Share2,
  FileText,
  BarChart,
  Magnet,
  UserPlus,
  ShoppingCart,
  ThumbsUp,
  ArrowDownUp,
  Megaphone
} from "lucide-react";

const InboundMarketing = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Inbound Marketing",
    "provider": {
      "@type": "Organization",
      "name": "erm-media"
    },
    "description": "Metodología de marketing digital que atrae clientes con contenido valioso: SEO, marketing de contenidos, email marketing y automatización.",
    "serviceType": "Marketing Digital",
    "areaServed": "ES"
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Inbound Marketing: Atrae, Convierte y Fideliza"
        description="Descubre el inbound marketing: atrae clientes con contenido valioso en lugar de publicidad invasiva. SEO, marketing de contenidos, email marketing y automatización."
        keywords="inbound marketing, marketing de contenidos, SEO, email marketing, lead generation, marketing digital, automatización de marketing"
        canonicalUrl="https://erm-media.com/inbound-marketing"
        structuredData={structuredData}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-background to-primary/5 pt-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Magnet className="w-4 h-4" />
              <span className="text-sm font-medium">Marketing Digital Estratégico</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Inbound Marketing:
              <span className="text-primary block">Atrae, Convierte y Fideliza</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              Descubre la metodología de marketing que atrae clientes de forma natural con contenido
              valioso, en lugar de interrumpirlos con publicidad invasiva. Genera leads cualificados
              y construye relaciones duraderas con tu audiencia.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#metodologia">
                <Button size="lg" className="group">
                  Conocer la Metodología
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <a href="#contacto">
                <Button size="lg" variant="outline">
                  Solicitar Estrategia
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ¿Qué es Inbound Marketing? */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
              ¿Qué es el Inbound Marketing?
            </h2>
            <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
              <p>
                El <strong className="text-foreground">inbound marketing</strong> es una metodología revolucionaria
                que se basa en <strong className="text-foreground">atraer clientes potenciales</strong> hacia tu negocio
                mediante contenido valioso y experiencias personalizadas, en lugar de interrumpirlos con publicidad
                tradicional invasiva.
              </p>
              <p>
                A diferencia del marketing tradicional (outbound), que busca activamente a los clientes con anuncios
                disruptivos, el inbound marketing crea contenido relevante que resuelve problemas reales de tu audiencia,
                convirtiéndote en una fuente de valor y confianza.
              </p>
              <p>
                Esta estrategia combina técnicas de <strong className="text-foreground">marketing de contenidos, SEO,
                redes sociales, email marketing</strong> y automatización para guiar a los usuarios a través de un viaje
                desde el descubrimiento inicial hasta convertirse en promotores leales de tu marca.
              </p>
              <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-lg">
                <p className="text-foreground font-semibold mb-2">
                  Dato clave:
                </p>
                <p className="text-muted-foreground">
                  Las empresas que implementan inbound marketing reducen su costo de adquisición de clientes
                  hasta un <strong className="text-primary">62%</strong> y experimentan tasas de conversión
                  <strong className="text-primary"> 10 veces superiores</strong> al marketing tradicional.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inbound vs Outbound */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Inbound vs Outbound Marketing
              </h2>
              <p className="text-lg text-muted-foreground">
                Entiende la diferencia entre ambas metodologías
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Inbound */}
              <Card className="border-primary/20 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Magnet className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Inbound Marketing</CardTitle>
                  <CardDescription>Marketing de Atracción</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Atrae</strong> clientes con contenido valioso y relevante
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>No invasivo:</strong> Los usuarios encuentran tu marca cuando la necesitan
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Costo-efectivo:</strong> Genera tráfico orgánico sostenible
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Construcción de confianza:</strong> Te posiciona como autoridad
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Leads cualificados:</strong> Atrae personas realmente interesadas
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Relaciones duraderas:</strong> Fomenta la lealtad del cliente
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Outbound */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mb-4">
                    <Megaphone className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-2xl">Outbound Marketing</CardTitle>
                  <CardDescription>Marketing Tradicional</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <ArrowDownUp className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Busca</strong> activamente a los clientes
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ArrowDownUp className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Invasivo:</strong> Interrumpe con publicidad no solicitada
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ArrowDownUp className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Costoso:</strong> Requiere inversión constante en medios
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ArrowDownUp className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Mensaje unidireccional:</strong> Comunicación de marca a consumidor
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ArrowDownUp className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Leads fríos:</strong> Alcanza audiencias no segmentadas
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ArrowDownUp className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Resultados a corto plazo:</strong> Efecto temporal
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Metodología Inbound */}
      <section id="metodologia" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                La Metodología Inbound: 4 Fases
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Un proceso integral que transforma desconocidos en promotores de tu marca
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Fase 1: Atraer */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Target className="w-6 h-6 text-blue-500" />
                  </div>
                  <CardTitle className="text-xl">1. Atraer</CardTitle>
                  <CardDescription>Atrae Desconocidos</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Genera tráfico cualificado hacia tu sitio web con contenido relevante y optimizado.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      Blog y contenido SEO
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      Redes sociales
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      Palabras clave estratégicas
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Fase 2: Convertir */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                    <UserPlus className="w-6 h-6 text-green-500" />
                  </div>
                  <CardTitle className="text-xl">2. Convertir</CardTitle>
                  <CardDescription>Visitantes a Leads</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Transforma visitantes en contactos capturando su información.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Landing pages optimizadas
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Formularios estratégicos
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Contenido descargable
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Fase 3: Cerrar */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center mb-4">
                    <ShoppingCart className="w-6 h-6 text-orange-500" />
                  </div>
                  <CardTitle className="text-xl">3. Cerrar</CardTitle>
                  <CardDescription>Leads a Clientes</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Convierte leads cualificados en clientes con nurturing efectivo.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                      Email marketing automatizado
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                      Lead scoring
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                      CRM integrado
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Fase 4: Deleitar */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Heart className="w-6 h-6 text-purple-500" />
                  </div>
                  <CardTitle className="text-xl">4. Deleitar</CardTitle>
                  <CardDescription>Clientes a Promotores</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Fideliza clientes convirtiéndolos en embajadores de tu marca.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                      Contenido post-venta
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                      Soporte proactivo
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                      Encuestas y feedback
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pilares del Inbound */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Los 6 Pilares del Inbound Marketing
              </h2>
              <p className="text-lg text-muted-foreground">
                Técnicas y estrategias que conforman una estrategia inbound completa
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>Marketing de Contenidos</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Crea contenido valioso que eduque, informe y entretenga a tu audiencia. Blogs,
                    ebooks, infografías, videos y más que resuelvan problemas reales.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Search className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>SEO (Search Engine Optimization)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Optimiza tu contenido para motores de búsqueda. Investigación de palabras clave,
                    optimización on-page y construcción de autoridad para posicionarte en Google.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Share2 className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>Redes Sociales</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Amplifica tu alcance y construye comunidad. Comparte contenido, interactúa con
                    tu audiencia y humaniza tu marca en las plataformas donde están tus clientes.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>Email Marketing</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Nutre relaciones con comunicación personalizada. Secuencias automatizadas,
                    newsletters y campañas segmentadas que guían al lead hacia la compra.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <BarChart className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>Análisis y Métricas</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Mide, analiza y optimiza constantemente. Monitorea KPIs, realiza tests A/B y
                    toma decisiones basadas en datos reales para mejorar resultados.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Lightbulb className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>Marketing Automation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Automatiza procesos repetitivos para escalar. Workflows inteligentes, lead
                    scoring automático y personalización a escala sin perder el toque humano.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Beneficios del Inbound Marketing
              </h2>
              <p className="text-lg text-muted-foreground">
                Por qué las empresas modernas apuestan por el inbound
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    ROI Superior y Costos Reducidos
                  </h3>
                  <p className="text-muted-foreground">
                    Genera tráfico orgánico sostenible que no requiere inversión publicitaria constante.
                    El costo por lead disminuye con el tiempo mientras tu biblioteca de contenido crece y sigue atrayendo visitantes.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Leads Más Cualificados
                  </h3>
                  <p className="text-muted-foreground">
                    Atrae personas que realmente están interesadas en tu producto o servicio. Los leads
                    inbound tienen mayor intención de compra porque te encontraron buscando soluciones a sus problemas.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <ThumbsUp className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Construcción de Autoridad
                  </h3>
                  <p className="text-muted-foreground">
                    Posiciónate como líder de pensamiento en tu industria. El contenido valioso y educativo
                    demuestra tu expertise y genera confianza antes de que el cliente considere comprar.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Heart className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Relaciones Duraderas
                  </h3>
                  <p className="text-muted-foreground">
                    Crea conexiones significativas con tu audiencia. El inbound no termina con la venta;
                    continúa deleitando clientes para convertirlos en promotores leales de tu marca.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <BarChart className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Resultados Medibles
                  </h3>
                  <p className="text-muted-foreground">
                    Trackea cada acción y optimiza constantemente. A diferencia del marketing tradicional,
                    puedes medir exactamente qué funciona y qué no, ajustando tu estrategia en tiempo real.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Escalabilidad y Crecimiento
                  </h3>
                  <p className="text-muted-foreground">
                    Tu contenido trabaja para ti 24/7. Un artículo bien optimizado puede generar leads
                    durante años sin costo adicional, creando un activo que se aprecia con el tiempo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proceso de Implementación */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Cómo Implementar una Estrategia Inbound
              </h2>
              <p className="text-lg text-muted-foreground">
                Pasos clave para comenzar con inbound marketing
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Define tu Buyer Persona
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Crea perfiles detallados de tu cliente ideal: demografía, comportamientos, retos,
                    objetivos y proceso de compra. Entender profundamente a quién te diriges es fundamental
                    para crear contenido relevante.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Mapea el Customer Journey
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Identifica las etapas por las que pasa tu cliente desde que descubre un problema hasta
                    que toma una decisión de compra. Esto te permitirá crear contenido específico para cada fase.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Desarrolla una Estrategia de Contenido
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Planifica qué contenido crear, para quién, cuándo y en qué formato. Define temas clave,
                    investiga palabras clave SEO y establece un calendario editorial consistente.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    4
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Optimiza tu Sitio Web
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Asegúrate de que tu web esté optimizada para conversiones: landing pages efectivas,
                    formularios estratégicos, CTAs claros y experiencia de usuario impecable.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    5
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Implementa Herramientas de Automatización
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Utiliza plataformas como HubSpot, Mailchimp o ActiveCampaign para automatizar el lead
                    nurturing, scoring y seguimiento. La tecnología te permite personalizar a escala.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    6
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Mide, Analiza y Optimiza
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Monitorea KPIs clave: tráfico, tasa de conversión, costo por lead, ROI. Realiza tests
                    A/B constantemente y ajusta tu estrategia basándote en datos reales, no suposiciones.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Estadísticas */}
      <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                El Poder del Inbound en Números
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  62%
                </div>
                <p className="text-muted-foreground">
                  Reducción en el costo de adquisición de clientes
                </p>
              </div>

              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  10x
                </div>
                <p className="text-muted-foreground">
                  Mayor tasa de conversión comparado con outbound
                </p>
              </div>

              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  3x
                </div>
                <p className="text-muted-foreground">
                  Más leads generados por cada dólar invertido
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section id="contacto" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              ¿Listo para Implementar Inbound Marketing?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Desarrollamos estrategias inbound personalizadas que generan resultados medibles.
              Solicita una consultoría gratuita y descubre cómo podemos ayudarte a atraer,
              convertir y fidelizar clientes de forma efectiva.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/#contacto">
                <Button size="lg" className="group">
                  Solicitar Consultoría Gratuita
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <a href="/soluciones-empresariales">
                <Button size="lg" variant="outline">
                  Ver Otras Soluciones
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InboundMarketing;
